<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/firebase'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'
import { useWizardStore } from '@/stores/wizard'
import type { MissionType, Agency } from '@/types'

const wizard = useWizardStore()

const nameBlurred = ref(false)

const MISSION_TYPES: { value: MissionType; label: string }[] = [
  { value: 'lost_person', label: 'Lost Person' },
  { value: 'rescue', label: 'Rescue' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'other', label: 'Other' },
]

function toggleMissionType(value: MissionType) {
  wizard.step1.mission_type = wizard.step1.mission_type === value ? null : value
}

function next() {
  nameBlurred.value = true
  if (!wizard.step1Valid) return
  wizard.currentStep = 2
}

// ── Requesting agency autocomplete ──────────────────────────────────────────

const agencies = ref<Agency[]>([])
// agencyInput is the live text while typing; store holds confirmed selection
const agencyInput = ref('')
const agencyDropdownOpen = ref(false)
const agencyContainerRef = ref<HTMLElement | null>(null)

// True once user has committed a selection (selected from list OR blurred with text)
const agencyConfirmed = computed(
  () => !!(wizard.step1.requesting_agency_id || wizard.step1.requesting_agency_name)
)

onMounted(async () => {
  const snap = await getDocs(collection(db, 'agencies'))
  agencies.value = snap.docs.map(d => ({ agency_id: d.id, ...d.data() } as Agency))
  document.addEventListener('mousedown', onClickOutside)
})

const filteredAgencies = computed(() => {
  const q = agencyInput.value.trim().toLowerCase()
  if (!q) return agencies.value
  return agencies.value.filter(a => a.agency_name.toLowerCase().includes(q))
})

function onAgencyInput(value: string) {
  agencyInput.value = value
  agencyDropdownOpen.value = true
}

function selectAgency(agency: Agency) {
  agencyInput.value = ''
  wizard.step1.requesting_agency_id = agency.agency_id
  wizard.step1.requesting_agency_name = agency.agency_name
  agencyDropdownOpen.value = false
}

function commitFreetext(name: string) {
  agencyInput.value = ''
  wizard.step1.requesting_agency_id = null
  wizard.step1.requesting_agency_name = name.trim()
  agencyDropdownOpen.value = false
}

function clearAgency() {
  agencyInput.value = ''
  wizard.step1.requesting_agency_id = null
  wizard.step1.requesting_agency_name = null
  agencyDropdownOpen.value = false
}

function onAgencyBlur() {
  setTimeout(() => {
    agencyDropdownOpen.value = false
    // Commit whatever is typed as freetext if not already confirmed
    if (agencyInput.value.trim() && !agencyConfirmed.value) {
      commitFreetext(agencyInput.value)
    }
  }, 150)
}

function onClickOutside(e: MouseEvent) {
  if (agencyContainerRef.value && !agencyContainerRef.value.contains(e.target as Node)) {
    agencyDropdownOpen.value = false
  }
}

onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside))
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Mission Identity</h2>
      <p class="font-mono text-xs text-outline mt-1">Step 1 of 4 — required</p>
    </div>

    <!-- Mission name -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Mission name <span class="text-error">*</span>
      </label>
      <FosInput
        v-model="wizard.step1.mission_name"
        placeholder="e.g. Lost hiker — Mt. Tamalpais"
        @blur="nameBlurred = true"
      />
      <p v-if="nameBlurred && !wizard.step1Valid" class="font-mono text-xs text-error">
        Mission name is required
      </p>
    </div>

    <!-- Mission number -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Mission number
        <span class="text-outline/50 font-normal normal-case tracking-normal">(optional)</span>
      </label>
      <div class="flex gap-2">
        <FosInput
          v-model="wizard.step1.mission_number"
          placeholder="e.g. 26-047"
          class="flex-1"
        />
        <FosButton
          variant="secondary"
          :disabled="wizard.generatingNumber"
          @click="wizard.generateMissionNumber()"
        >
          {{ wizard.generatingNumber ? '...' : 'Auto' }}
        </FosButton>
      </div>
      <p class="font-mono text-[0.65rem] text-outline">
        AUTO generates a sequential number from your agency's records.
      </p>
    </div>

    <!-- Mission type -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Mission type
      </label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="mt in MISSION_TYPES"
          :key="mt.value"
          type="button"
          class="px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold transition-colors focus:outline-none"
          :class="
            wizard.step1.mission_type === mt.value
              ? 'bg-primary-container text-white'
              : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest'
          "
          @click="toggleMissionType(mt.value)"
        >
          {{ mt.label }}
        </button>
      </div>
    </div>

    <!-- Requesting agency -->
    <div ref="agencyContainerRef" class="flex flex-col gap-1.5 relative">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Requesting agency
        <span class="text-outline/50 font-normal normal-case tracking-normal">(optional)</span>
      </label>

      <!-- Confirmed: known agency with logo -->
      <div
        v-if="wizard.step1.requesting_agency_id"
        class="flex items-center gap-2 px-3 py-2 bg-surface-container-high border border-border rounded"
      >
        <img
          v-if="agencies.find(a => a.agency_id === wizard.step1.requesting_agency_id)?.agency_logo"
          :src="agencies.find(a => a.agency_id === wizard.step1.requesting_agency_id)!.agency_logo"
          class="h-5 w-5 object-contain rounded-sm flex-shrink-0"
          alt=""
        />
        <span class="font-mono text-sm text-on-surface flex-1">{{ wizard.step1.requesting_agency_name }}</span>
        <button type="button" class="font-mono text-xs text-outline hover:text-on-surface transition-colors" @click="clearAgency">✕</button>
      </div>

      <!-- Confirmed: freetext (no matching agency) -->
      <div
        v-else-if="wizard.step1.requesting_agency_name"
        class="flex items-center gap-2 px-3 py-2 bg-surface-container-high border border-border rounded"
      >
        <span class="font-mono text-sm text-on-surface flex-1">{{ wizard.step1.requesting_agency_name }}</span>
        <span class="font-mono text-[0.6rem] uppercase tracking-widest text-outline px-1.5 py-0.5 bg-surface-container-highest rounded">not in system</span>
        <button type="button" class="font-mono text-xs text-outline hover:text-on-surface transition-colors" @click="clearAgency">✕</button>
      </div>

      <!-- Input (shown until a selection is confirmed) -->
      <FosInput
        v-else
        :model-value="agencyInput"
        placeholder="Search or type agency name"
        @update:model-value="onAgencyInput"
        @focus="agencyDropdownOpen = true"
        @blur="onAgencyBlur"
      />

      <!-- Dropdown -->
      <div
        v-if="agencyDropdownOpen && !agencyConfirmed && (filteredAgencies.length > 0 || agencyInput.trim())"
        class="absolute top-full left-0 right-0 z-50 mt-1 bg-surface border border-border rounded shadow-lg max-h-48 overflow-y-auto"
      >
        <button
          v-for="agency in filteredAgencies"
          :key="agency.agency_id"
          type="button"
          class="w-full flex items-center gap-3 px-3 py-2 hover:bg-surface-container-low transition-colors text-left"
          @mousedown.prevent="selectAgency(agency)"
        >
          <img
            v-if="agency.agency_logo"
            :src="agency.agency_logo"
            class="h-6 w-6 object-contain rounded-sm flex-shrink-0"
            alt=""
          />
          <div v-else class="h-6 w-6 bg-surface-container-highest rounded-sm flex-shrink-0" />
          <span class="font-mono text-sm text-on-surface">{{ agency.agency_name }}</span>
        </button>

        <!-- Freetext option when no exact match -->
        <button
          v-if="agencyInput.trim() && !agencies.find(a => a.agency_name.toLowerCase() === agencyInput.trim().toLowerCase())"
          type="button"
          class="w-full flex items-center gap-3 px-3 py-2 hover:bg-surface-container-low transition-colors text-left border-t border-border"
          @mousedown.prevent="commitFreetext(agencyInput)"
        >
          <span class="font-mono text-xs text-outline uppercase tracking-widest">Use</span>
          <span class="font-mono text-sm text-on-surface">"{{ agencyInput.trim() }}"</span>
        </button>
      </div>
    </div>

    <!-- Start date + time -->
    <div class="flex gap-4">
      <div class="flex flex-col gap-1.5 flex-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">Start date</label>
        <input
          v-model="wizard.step1.start_date"
          type="date"
          class="w-full px-3 py-2 bg-surface-container-high text-on-surface font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
      </div>
      <div class="flex flex-col gap-1.5 flex-1">
        <label class="font-mono text-xs uppercase tracking-widest text-outline">Start time</label>
        <input
          v-model="wizard.step1.start_time"
          type="time"
          class="w-full px-3 py-2 bg-surface-container-high text-on-surface font-mono text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex justify-end pt-2">
      <FosButton @click="next">Next →</FosButton>
    </div>
  </div>
</template>
